/**
 * Pure helpers for hydrating a GIWL quote from landing-page URL parameters.
 *
 * The GIWL flow can deep-link straight into the application with the quote
 * encoded in the URL. These helpers isolate the parsing, state resolution, and
 * payload building so the page effect stays small and the branching is testable.
 */
import { getStateAbbreviation } from "services/cdsService";
import { isoDateStringToDate } from "utils/age";
import { GIWLQuoteParamsSchema, type ParsedGIWLQuoteParams } from "types/Card";

export type { ParsedGIWLQuoteParams };

export type ParseGIWLQuoteResult = { valid: true; params: ParsedGIWLQuoteParams } | { valid: false };

export interface ParsedGIWLCampaignParams {
    clubCode?: string;
    leadSource: string;
    campaignCode: string;
}

/**
 * Fallback campaign code for direct GIWL applications (mail keycode), ported from
 * the legacy giwl-enrollment flow. Exactly 10 chars, so it also satisfies the
 * applicant.keyCode size constraint (must be 10).
 */
const DEFAULT_CAMPAIGN_CODE = "18LW99901O";

/** Fallback lead-source code for direct GIWL applications, ported from legacy. */
const DEFAULT_LEAD_SOURCE_CODE = "67";

/**
 * Extracts the campaign-tracking params from a GIWL landing-page query string,
 * mirroring the cc→clubCode, sc→leadSource, cmp→campaignCode mapping the DTO/MLTA
 * invitation flow uses (see useAutomaticOfferLookup). leadSource and campaignCode
 * fall back to the legacy mail-keycode defaults when absent (GIWL deep-links into
 * the application without populating these); clubCode is left unset so callers can
 * merge without clobbering existing data.
 */
export const parseGIWLCampaignParams = (paramsKey: string): ParsedGIWLCampaignParams => {
    const searchParams = new URLSearchParams(paramsKey);
    const clubCode = searchParams.get("cc") ?? undefined;

    const campaign: ParsedGIWLCampaignParams = {
        leadSource: searchParams.get("sc") || DEFAULT_LEAD_SOURCE_CODE,
        campaignCode: searchParams.get("cmp") || DEFAULT_CAMPAIGN_CODE,
    };
    if (clubCode) campaign.clubCode = clubCode;
    return campaign;
};

/** Quote-identifying param keys that only ever appear on a GIWL landing URL. */
const GIWL_QUOTE_PARAM_KEYS = ["gender", "dob", "zipCode", "coverage", "premium", "email"] as const;

/**
 * True when the query string carries any GIWL quote param, i.e. it represents a
 * landing attempt — even an incomplete/invalid one — rather than internal
 * navigation that arrives with no quote params at all. Lets callers tell a fresh
 * landing (which must be validated) apart from back-navigation (which must not
 * clobber an in-progress application).
 */
export const hasGIWLQuoteParams = (paramsKey: string): boolean => {
    const searchParams = new URLSearchParams(paramsKey);
    return GIWL_QUOTE_PARAM_KEYS.some((key) => searchParams.has(key));
};

/**
 * Parses and validates the GIWL quote params from a query string against GIWLQuoteParamsSchema.
 * Returns `{ valid: false }` when any field is missing or invalid so the caller can route to the error page.
 */
export const parseGIWLQuoteParams = (paramsKey: string): ParseGIWLQuoteResult => {
    const searchParams = new URLSearchParams(paramsKey);
    const rawEmail = searchParams.get("email");
    // Defensively decode in case the value still contains percent-encoded chars (e.g. %40 for @)
    const email = rawEmail ? decodeURIComponent(rawEmail) : rawEmail;

    const candidate = {
        gender: searchParams.get("gender") ?? undefined,
        dob: searchParams.get("dob") ?? undefined,
        zipCode: searchParams.get("zipCode") ?? undefined,
        // Treat a missing or empty state as "resolve from ZIP" rather than an invalid value.
        stateFromParams: searchParams.get("state") || null,
        coverage: searchParams.get("coverage") ?? undefined,
        premium: searchParams.get("premium") ?? undefined,
        email: email ?? undefined,
        isMember: searchParams.get("isMember") === "true",
        policyType: searchParams.get("policy_type") ?? undefined,
    };

    const result = GIWLQuoteParamsSchema.safeParse(candidate);
    if (!result.success) {
        return { valid: false };
    }

    return { valid: true, params: result.data };
};

/**
 * Resolves the applicant's state and zip for a GIWL quote.
 * New York is unsupported, so it clears both fields. When no state is supplied,
 * it looks one up from the zip code, falling back to empty on failure.
 */
export const resolveGIWLState = async (
    stateFromParams: string | null,
    zipCode: string
): Promise<{ state: string; zipCode: string }> => {
    if (stateFromParams?.toUpperCase() === "NY") {
        return { state: "", zipCode: "" };
    }

    if (stateFromParams) {
        return { state: stateFromParams, zipCode };
    }

    try {
        const stateAbbreviation = await getStateAbbreviation(zipCode);
        return stateAbbreviation === "NY" ? { state: "", zipCode: "" } : { state: stateAbbreviation, zipCode };
    } catch {
        return { state: "", zipCode };
    }
};

/**
 * Builds the `saveQuote` payload from parsed params and the resolved state/zip.
 */
export const buildGIWLQuotePayload = (params: ParsedGIWLQuoteParams, state: string, resolvedZipCode: string) => {
    const dobDate = isoDateStringToDate(params.dob);

    return {
        state,
        email: params.email,
        dobMonth: dobDate ? String(dobDate.getMonth() + 1).padStart(2, "0") : "",
        dobDay: dobDate?.getDate().toString() || "",
        dobYear: dobDate?.getFullYear().toString() || "",
        applicantType: "member" as "member" | "spouse",
        zipCode: resolvedZipCode,
        selectedCoverageAmount: params.coverage as unknown as number,
        selectedCoveragePremium: params.premium as unknown as number,
        gender: params.gender,
        coverageOptions: [
            { coverageAmount: params.coverage as unknown as number, monthlyPremium: params.premium as unknown as number },
        ],
        isMember: params.isMember,
        policyType: params.policyType,
    };
};
